import { ArrowLineDownIcon, ArrowLineUpIcon } from '@phosphor-icons/react';
import { RECURRING_TAXONOMY, type TaxonomyConfig } from '@/components/recurring-payments/taxonomy';
import type { RecurrenceFilter, TransactionDirection } from '@/lib/transactions/types';

// Reuse the taxonomy's exact icon component type so the shared (Phosphor) icons — including
// the taxonomy's own — assign without ComponentType/defaultProps invariance friction.
type IconType = TaxonomyConfig['icon'];

// ── Direction (single-select toggle) ────────────────────────────────────────────────────────
// Entradas → isBankIncome (bank income); Saídas → isExpense. Mapping to API params happens in the
// screen; here we only describe the options.

export interface DirectionOption {
  value: TransactionDirection;
  label: string;
  Icon: IconType;
}

export const DIRECTION_OPTIONS: DirectionOption[] = [
  { value: 'entradas', label: 'Entradas', Icon: ArrowLineDownIcon },
  { value: 'saidas', label: 'Saídas', Icon: ArrowLineUpIcon },
];

export const VALID_DIRECTIONS: ReadonlySet<string> = new Set(DIRECTION_OPTIONS.map((o) => o.value));

/** Direction label per token, derived from DIRECTION_OPTIONS so the picker and the active chip
 *  never drift (mirrors RECURRENCE_CHIP_LABEL for the recurrence axis). */
export const DIRECTION_CHIP_LABEL = Object.fromEntries(
  DIRECTION_OPTIONS.map((o) => [o.value, o.label]),
) as Record<TransactionDirection, string>;

// ── Recurrence (multi-select) ───────────────────────────────────────────────────────────────
// The three groups mirror the Recorrentes page. Each token's value is sent verbatim as a repeatable
// `tipo` query param; the API OR-unions them. No client-side param mapping — the token IS the wire
// value — so a single source of truth for the set.

export interface RecurrenceOption {
  value: RecurrenceFilter;
  label: string;
  Icon: IconType;
}

/** Picker options in order, reusing the Recurring Payments page's taxonomy label + icon verbatim. */
export const RECURRENCE_OPTIONS: RecurrenceOption[] = [
  { value: 'fixas', label: RECURRING_TAXONOMY.fixas.label, Icon: RECURRING_TAXONOMY.fixas.icon },
  { value: 'assinaturas', label: RECURRING_TAXONOMY.assinaturas.label, Icon: RECURRING_TAXONOMY.assinaturas.icon },
  { value: 'parcelamentos', label: RECURRING_TAXONOMY.parcelamentos.label, Icon: RECURRING_TAXONOMY.parcelamentos.icon },
];

/** Short chip label per recurrence token. */
export const RECURRENCE_CHIP_LABEL: Record<RecurrenceFilter, string> = {
  fixas: RECURRING_TAXONOMY.fixas.label, // Contas fixas
  assinaturas: RECURRING_TAXONOMY.assinaturas.label, // Assinaturas
  parcelamentos: RECURRING_TAXONOMY.parcelamentos.label, // Parcelamentos
};

export const VALID_RECURRENCE: ReadonlySet<string> = new Set(RECURRENCE_OPTIONS.map((o) => o.value));

/**
 * Normalise a raw list of URL values to a deduped set of valid recurrence tokens in **canonical**
 * (RECURRENCE_OPTIONS) order — so `?rec=assinaturas&rec=fixas` and `?rec=fixas&rec=assinaturas`
 * collapse to one representation, giving the same first-chip label and the same SWR cache key.
 */
export function sanitizeRecurrence(raw: readonly string[]): RecurrenceFilter[] {
  const present = new Set(raw);
  return RECURRENCE_OPTIONS.map((o) => o.value).filter((v) => present.has(v));
}
